/** Chips and status pills. Chips tint an admin-chosen colour; status pills carry a fixed tone and an icon. */
import type { ReactNode } from "react";
import { cn, textOn } from "../lib/cn";

export function Chip({ color, children, solid, className, title, icon }: { color?: string | null; children: ReactNode; solid?: boolean; className?: string; title?: string; icon?: ReactNode }) {
  const c = color ?? "#8C8C8C";
  const style = solid
    ? { background: c, color: textOn(c), borderColor: c }
    : { background: `color-mix(in srgb, ${c} 13%, #FDFDFD)`, borderColor: `color-mix(in srgb, ${c} 32%, #FDFDFD)`, color: "#1f2a14" };
  return (
    <span title={title} style={style} className={cn("inline-flex max-w-full items-center gap-1.5 truncate rounded-full border px-2 py-[1px] text-[12px] leading-5 font-semibold whitespace-nowrap", className)}>
      {!solid && !icon && <span className="size-1.5 shrink-0 rounded-full" style={{ background: c }} />}
      {icon}
      <span className="truncate">{children}</span>
    </span>
  );
}

export type PillTone = "success" | "warn" | "danger" | "stop" | "info" | "neutral" | "moss";

const TONE: Record<PillTone, string> = {
  success: "bg-success-bg text-success-ink border-success/25",
  warn: "bg-warn-bg text-terra-text border-terra-deep/25",
  danger: "bg-danger-bg text-danger-ink border-danger/25",
  stop: "bg-danger text-paper border-danger",
  info: "bg-info-bg text-info border-info/25",
  neutral: "bg-cream-deep text-muted border-line",
  moss: "bg-sage-50 text-olive border-sage-200",
};

/** A status with an icon, in the hub's risk-badge style. */
export function StatusPill({ tone, icon, children, className, title }: { tone: PillTone; icon?: ReactNode; children: ReactNode; className?: string; title?: string }) {
  return (
    <span title={title} className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-[1px] text-[12px] leading-5 font-bold whitespace-nowrap [&_svg]:size-3.5 [&_svg]:shrink-0", TONE[tone], className)}>
      {icon}
      {children}
    </span>
  );
}
